/**
 * Generate a campaign contract PDF using browser print.
 *
 * The HTML body is fetched from the stored
 * `influencer_host_campaign_contract` document template (managed via
 * the admin dashboard) and rendered through the placeholder
 * substitution engine. Opens a new window with the rendered HTML and
 * triggers the print dialog (Save as PDF). Fully supports Korean and
 * all Unicode characters.
 *
 * The exported signature is preserved so the call sites in
 * `CollaborationAcceptPage.tsx` and `MyCampaignsPage.tsx` continue to
 * invoke `generateCampaignContractPdf(params)` without change.
 *
 * See `.kiro/specs/document-template-management/design.md` §6.10.
 */
import toast from 'react-hot-toast';
import i18next from 'i18next';
import apolloClient from '../graphql/apollo-client';
import { GET_DOCUMENT_TEMPLATE_CONTENT } from '../graphql/queries';
import { renderTemplate } from './templateRenderer';
import {
  getCachedTemplate,
  setCachedTemplate,
} from './documentTemplateCache';
import { INFLUENCER_PAYOUT_RATE } from '../constants/platform';

const DOC_TYPE = 'influencer_host_campaign_contract' as const;

interface DocumentTemplateRow {
  docType: string;
  content: string;
}

/**
 * Format a date-ish value to `YYYY-MM-DD`. Accepts ISO timestamps
 * (`2026-07-10T12:34:56.789Z`) as well as already date-only strings.
 * Returns an empty string for empty/invalid input so the contract never
 * shows a raw ISO timestamp or `Invalid Date`.
 */
function toYmd(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export async function generateCampaignContractPdf(params: {
  campaign: any;
  userName: string;
  selectedMissions: string[];
  visitStartDate: string;
  visitEndDate: string;
  requestedAt?: string;
  respondedAt?: string;
}): Promise<void> {
  const {
    campaign: c,
    userName,
    selectedMissions,
    visitStartDate,
    visitEndDate,
    requestedAt,
    respondedAt,
  } = params;

  // 1) Fetch-or-cache the stored template. On failure, surface a
  //    toast and bail without opening a print window.
  let templateContent = getCachedTemplate(DOC_TYPE);
  if (templateContent === undefined) {
    try {
      const result = await apolloClient.query<{
        getDocumentTemplateContent: DocumentTemplateRow | null;
      }>({
        query: GET_DOCUMENT_TEMPLATE_CONTENT,
        variables: { docType: DOC_TYPE },
        fetchPolicy: 'network-only',
      });
      const row = result.data?.getDocumentTemplateContent;
      if (!row) {
        toast.error(i18next.t('admin:documentTemplates.loadFailed.contract'));
        return;
      }
      templateContent = row.content;
      setCachedTemplate(DOC_TYPE, templateContent);
    } catch {
      toast.error(i18next.t('admin:documentTemplates.loadFailed.contract'));
      return;
    }
  }

  // 2) Build the Placeholder_Context. `context.campaign` is enriched
  //    with computed fields (e.g. net reward after the payout rate) so
  //    the template can reference them without needing custom logic
  //    in the engine.
  const rewardAmountNet = c?.rewardAmount
    ? Math.round(c.rewardAmount * INFLUENCER_PAYOUT_RATE)
    : 0;
  const enrichedCampaign = {
    ...c,
    rewardAmountNet,
  };
  const context = {
    campaign: enrichedCampaign,
    match: {
      visitStartDate: toYmd(visitStartDate),
      visitEndDate: toYmd(visitEndDate),
      requestedAt: toYmd(requestedAt),
      respondedAt: toYmd(respondedAt),
      selectedMissions,
    },
    influencer: { name: userName },
    host: {},
    brand: {},
  };

  // 3) Render the template against the context. The stored template
  //    includes its own `<!DOCTYPE html><html>...<body>...</body></html>`
  //    scaffolding, so the output is the complete HTML document.
  const html = renderTemplate(templateContent, context);

  // 4) Hand off to the existing print window flow.
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
}
